import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Apple, Beef, Croissant, Milk, Sparkles, Send } from "lucide-react";

const IMAGES = {
  produce:
    "https://images.pexels.com/photos/35770688/pexels-photo-35770688.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1100",
  carniceria: "/images/chicharron.jpg",
  panaderia: "/images/panaderia.jpg",
  cremeria: "/images/queso.jpg",
  accesorios: "/images/accesorios-stitch.png",
  remesas:
    "https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1100",
};

const ICONS = {
  produce: Apple,
  carniceria: Beef,
  panaderia: Croissant,
  cremeria: Milk,
  accesorios: Sparkles,
  remesas: Send,
};

const ACCESORIOS_COLLAGE = [
  "/images/accesorios-stitch.png",
  "/images/accesorios-cowgirl.png",
];

// 6-card bento on a 6-col grid:
// Row 1: 4 + 2 (Produce, Carniceria)
// Row 2: 2 + 2 + 2 (Panaderia, Cremeria, Accesorios)
// Row 3: 6 (Remesas — full-width service banner)
const SPANS = [
  "md:col-span-4",
  "md:col-span-2",
  "md:col-span-2",
  "md:col-span-2",
  "md:col-span-2",
  "md:col-span-6",
];

export default function Departments() {
  const { t } = useLanguage();
  const items = t("departments.items");

  return (
    <section
      id="departments"
      data-testid="departments-section"
      className="relative bg-[#EADCCF] py-24 md:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="max-w-3xl mb-14">
          <span className="text-[12px] uppercase tracking-[0.22em] text-[#C05A3A] font-medium">
            {t("departments.eyebrow")}
          </span>
          <h2 className="font-serif-display text-[#232321] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-4">
            {t("departments.title")}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6 auto-rows-[260px] md:auto-rows-[320px]">
          {items.map((item, i) => {
            const Icon = ICONS[item.key];
            const span = SPANS[i] || "md:col-span-2";
            const img = IMAGES[item.key];
            const isAccesorios = item.key === "accesorios";

            return (
              <article
                key={item.key}
                data-testid={`department-card-${item.key}`}
                className={`group relative ${span} overflow-hidden rounded-2xl border border-[#E2D9CD]`}
              >
                {isAccesorios ? (
                  <>
                    <div className="absolute inset-0 grid grid-cols-2 gap-0.5 bg-[#232321]">
                      {ACCESORIOS_COLLAGE.map((src, ci) => (
                        <img
                          key={src}
                          src={src}
                          alt={`${item.name} ${ci + 1}`}
                          data-testid={`accesorios-collage-img-${ci}`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      ))}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#232321]/90 via-[#232321]/35 to-transparent" />
                  </>
                ) : img ? (
                  <>
                    <img
                      src={img}
                      alt={item.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#232321]/85 via-[#232321]/30 to-transparent" />
                  </>
                ) : (
                  // Solid decorative gradient for departments with no photo yet
                  <div className="absolute inset-0 bg-gradient-to-br from-[#E6A85C] via-[#C05A3A] to-[#7d3b54]">
                    <div className="absolute inset-0 opacity-30 mix-blend-overlay grain-overlay" />
                    <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#F9F6F0]/20 blur-2xl" />
                    <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-[#232321]/20 blur-2xl" />
                  </div>
                )}

                <div className="relative h-full flex flex-col justify-end p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="h-10 w-10 rounded-full bg-[#F9F6F0]/15 backdrop-blur-md border border-[#F9F6F0]/25 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[#F9F6F0]" strokeWidth={1.5} />
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#E6A85C]">
                      0{i + 1}
                    </span>
                    {isAccesorios && (
                      <span
                        data-testid="accesorios-social-soon"
                        className="ml-auto text-[10px] uppercase tracking-[0.22em] rounded-full bg-[#F9F6F0]/15 border border-[#F9F6F0]/25 px-2.5 py-1 text-[#F9F6F0]"
                      >
                        Social · soon
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif-display text-[#F9F6F0] text-3xl md:text-4xl tracking-tight">
                    {item.name}
                  </h3>
                  <p className="text-[#F9F6F0]/85 text-sm md:text-[15px] mt-2 max-w-md leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
